import { useEffect, useState } from "react";

/**
 * Measures an element's content-box width in CSS pixels, 0 until it lands.
 * Returns the width and a **callback ref** to put on the element.
 *
 * The trend chart draws its SVG at 1:1, so viewBox units are CSS pixels and
 * it cannot lay anything out before it knows how wide it actually is.
 *
 * A callback ref rather than a `RefObject`: the measured element sits behind
 * a loading branch, so it mounts *after* the first render. An effect keyed on
 * a ref object would have run once against a null ref and never re-run, and
 * the chart would stay blank forever.
 */
export function useElementWidth(): [
  number,
  (node: HTMLElement | null) => void,
] {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (node === null) return;
    setWidth(node.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry !== undefined) setWidth(entry.contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  // `setNode` is stable, so using it as a ref callback never re-attaches.
  return [width, setNode];
}
